import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The billing alert banner, in the design's four states.
 *
 * `healthy` renders nothing at all: a banner that is always there is a banner nobody reads.
 * The other three are distinguished by tone as well as by copy, because "your card was
 * declined" and "you are nearly out of credit" need different urgency and the same shape.
 *
 * The balance itself comes from P10-04's `packages/payments`, which owns the meter and the
 * ledger. This component owns only how it is told. AMENDED 2026-10-04: the figure is Available (the
 * Billing screen's headline, the one admission refuses against) and says so; when new conversations
 * are refused only because one in progress holds the credit, the banner says that instead of "stopped".
 *
 * AMENDED 2026-10-06 (plan-014 E5, FR12-PST-04): a POSTPAID balance in recovery adds its own banner — the due date's
 * banner step puts the balance in `grace` ("Your October invoice is due" / "overdue", with Pay now → Finance →
 * Postpaid, where the hosted payment page opens), and the nonpayment suspension at due + 14 says new paid calls have
 * stopped and that paying restores them. It reads the organization's balances and the postpaid one's facts itself
 * (Finance → Postpaid's own reads), so the shell's call site is unchanged; a person without finance read sees none.
 *
 * AMENDED 2026-10-12 (plan-017 WAPP-A, design §29.11 row 6, owner answer OD-C): the low banner adds how many minutes
 * Available buys at the organization's highest rate in force, naming that rate (`lowCreditDekOf`); the money is
 * unchanged, and the line it compares against is the API's (`highest_talk_rate_minor_per_minute` × the configured
 * low-balance minutes, in `getBillingState`).
 *
 * AMENDED 2026-10-17 (plan-020 WAPP-BILL, design §32.17 "Banner", Q-25/Q-26): an unpaid plan renewal adds its own
 * banner (`PlanRenewalAlert`, Billing's) with "Pay now" and "Replace card"; it reads the organization's plan itself, so
 * the shell's call site is unchanged.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { money, type CurrencyCode, type Money } from "@wetalk/domain";
import { Button, Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { formatMoney, formatTrialRemaining } from "../money.js";
import type { BillingStateView } from "../api/gateway.js";
import { talkMinuteAffordableOf, talkRateText, useTalkRates } from "../api/slice/talk-rate.js";
import { useGateway } from "../api/provider.js";
import type { PostpaidView } from "../api/slice/postpaid.js";
import { roleHolds, useOpenAccount } from "../routes/account-admin/overview/account-overview-screen.js";
import { PlanRenewalAlert } from "../routes/billing/plan-renewal-alert.js";

export interface BillingAlertProps {
  readonly billing: BillingStateView;
  readonly onAddCredit: () => void;
}

interface AlertCopy {
  readonly className: string;
  readonly title: string;
  readonly dek: string;
  readonly primaryLabel: string;
}

/**
 * The low banner's sentence about money: Available, and — once the organization's rates are read — how many minutes
 * it buys at its HIGHEST rate, naming that rate (plan-017 WAPP-A, design §29.11 row 6, owner answer OD-C). Until the
 * rates are read, or when the highest rate is free, it states the money alone — never minutes at a guessed rate.
 */
export function lowCreditDekOf(balance: Money, highest: Money | null): string {
  const minute = highest === null ? null : talkMinuteAffordableOf(balance, highest);
  if (highest === null || minute === null) {
    return portalText("shell", "value_available_agents_stop_answering_at_zero_nothing_is_c_9f2e41d3", { v0: String(formatMoney(balance)) });
  }
  return portalText("core", "talk_rate.banner_available", {
    value0: formatMoney(balance),
    value1: portalText("core", "talk_rate.minutes_at_highest", { count: minute, value1: talkRateText(highest, "minute") }),
  });
}

function copyFor(billing: BillingStateView, highest: Money | null): AlertCopy | null {
  switch (billing.health) {
    case "healthy":
      return null;
    case "low":
      return {
        className: "c-alert c-alert-low",
        title: portalText("shell", "you_are_nearly_out_of_talk_time_d556c7f5"),
        // 2026-10-04: the figure is Available — the Billing screen's headline, under the same word.
        dek:
          billing.trialSecondsRemaining === null
            ? lowCreditDekOf(billing.balance, highest)
            : portalText("shell", "value_of_free_talk_time_left_agents_stop_answering_at_zero_076be106", { v0: String(formatTrialRemaining(billing.trialSecondsRemaining)) }),
        primaryLabel: portalText("shell", "add_credit_e89c5ab0"),
      };
    case "declined":
      return {
        className: "c-alert c-alert-critical",
        title: portalText("shell", "your_last_payment_was_declined_29c3cb0a"),
        dek: portalText("shell", "nothing_has_stopped_yet_update_the_card_before_the_balance_9faa18c2"),
        primaryLabel: portalText("shell", "update_the_card_f8f349cb"),
      };
    case "out-of-credit":
      // 2026-10-04: refused only because a conversation in progress holds what is left — the API's
      // own sentence says so, and that it frees up when that conversation ends.
      if (billing.refusal?.cause === "held_by_open_conversation") {
        return {
          className: "c-alert c-alert-low",
          title: portalText("shell", "a_conversation_in_progress_is_holding_your_remaining_talk_791fbf50"),
          dek: billing.refusal.message,
          primaryLabel: portalText("shell", "add_credit_e89c5ab0"),
        };
      }
      return {
        className: "c-alert c-alert-critical",
        title: portalText("shell", "your_agents_have_stopped_answering_533409a6"),
        dek: portalText("shell", "the_balance_is_zero_and_nothing_is_charged_automatically_a_99a267a7"),
        primaryLabel: portalText("shell", "add_credit_e89c5ab0"),
      };
    default:
      return null;
  }
}

export function BillingAlert({ billing, onAddCredit }: BillingAlertProps): ReactNode {
  usePortalTranslation("shell");
  return (
    <>
      <PrepaidBillingAlert billing={billing} onAddCredit={onAddCredit} />
      <PostpaidCollectionAlert />
      <PlanRenewalAlert />
    </>
  );
}

const MONTH = () => ([portalText("shell", "january_7a22d73d"), portalText("shell", "february_5c3a35ef"), portalText("shell", "march_433632ea"), portalText("shell", "april_a0393902"), portalText("shell", "may_c94f4798"), portalText("shell", "june_a9db9067"), portalText("shell", "july_df97a425"), portalText("shell", "august_69d97c57"), portalText("shell", "september_1c542e79"), portalText("shell", "october_87206ae2"), portalText("shell", "november_3c5bf776"), portalText("shell", "december_65640c65")] as const);

/** The postpaid banner's copy, or `null` when the balance is not in recovery. Pure. */
export function postpaidAlertCopyOf(view: PostpaidView): AlertCopy | null {
  if (view.contract === null) return null;
  const open = view.openInvoice[0];
  const month = open === undefined ? "" : (MONTH()[Number(open.yearMonth.slice(5, 7)) - 1] ?? open.yearMonth);
  if (view.collectionState === "suspended_nonpayment") {
    return {
      className: "c-alert c-alert-critical",
      title: portalText("shell", "new_paid_calls_have_stopped_fad6b62f"),
      dek: portalText("shell", "value_is_overdue_paying_restores_new_calls_straight_away_p_7c6f1d84", { v0: month === "" ? portalText("shell", "postpaid.anInvoice") : portalText("shell", "postpaid.monthInvoice", { month }) }),
      primaryLabel: portalText("shell", "pay_now_748c3235"),
    };
  }
  if (view.collectionState === "grace" && open !== undefined) {
    return {
      className: "c-alert c-alert-low",
      title: open.overdue ? portalText("shell", "your_value_invoice_is_overdue_7b680e17", { v0: String(month) }) : portalText("shell", "your_value_invoice_is_due_d5549e8f", { v0: String(month) }),
      dek: open.paymentPending
        ? portalText("shell", "a_card_payment_is_in_progress_this_goes_away_once_the_bank_1dc3e94e")
        : portalText("shell", "value_including_vat_pay_by_card_or_by_bank_transfer_with_t_836fdefa", { v0: String(formatMoney(money(open.grossDueMinor, open.currency as CurrencyCode))) }),
      primaryLabel: portalText("shell", "pay_now_748c3235"),
    };
  }
  return null;
}

/** The postpaid banner (plan-014 E5): the organization's postpaid balance, when it is in recovery. */
function PostpaidCollectionAlert(): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const navigate = useNavigate();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const balances = useQuery({
    queryKey: ["postpaid", "balances", accountId ?? "none"],
    queryFn: () => gateway.listBillingAccounts(accountId ?? ""),
    enabled: accountId !== null && canRead,
    staleTime: 300_000,
    retry: false,
  });
  const postpaidId = (balances.data ?? []).find((one) => one.collectionPolicy === "postpaid")?.billingAccountId ?? null;
  const view = useQuery({
    queryKey: ["postpaid", "view", postpaidId ?? "none"],
    queryFn: () => gateway.readPostpaid(postpaidId ?? ""),
    enabled: postpaidId !== null,
    staleTime: 300_000,
    retry: false,
  });
  if (view.data === undefined) return null;
  const copy = postpaidAlertCopyOf(view.data);
  if (copy === null) return null;
  return (
    <div className={copy.className} role="status">
      <Icon glyph={GLYPH.warningCircle} size="xl" tone={copy.className.includes("critical") ? "critical" : "caution"} />
      <span className="c-alert-body">
        <span className="c-alert-title">{copy.title}</span>
        <span className="c-alert-dek">{copy.dek}</span>
      </span>
      <span className="c-alert-actions">
        <Button
          variant="primary"
          onClick={() => {
            void navigate({ to: "/finance/postpaid" });
          }}
        >
          {copy.primaryLabel}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: "/billing" });
          }}
        >
          {portalText("shell", "billingAlert.billing")}
        </Button>
      </span>
    </div>
  );
}

function PrepaidBillingAlert({ billing, onAddCredit }: BillingAlertProps): ReactNode {
  usePortalTranslation("shell");
  const navigate = useNavigate();
  const rates = useTalkRates();
  const copy = copyFor(billing, rates.data?.highest ?? null);
  if (copy === null) return null;

  return (
    <div className={copy.className} role="status">
      <Icon
        glyph={billing.health === "low" ? GLYPH.warningCircle : GLYPH.phoneX}
        size="xl"
        tone={billing.health === "low" ? "caution" : "critical"}
      />
      <span className="c-alert-body">
        <span className="c-alert-title">{copy.title}</span>
        <span className="c-alert-dek">{copy.dek}</span>
      </span>
      <span className="c-alert-actions">
        <Button variant="primary" onClick={onAddCredit}>
          {copy.primaryLabel}
        </Button>
        {/* The prototype's second button, beside the primary on every state: "Billing". */}
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: "/billing" });
          }}
        >
          {portalText("shell", "billingAlert.billing")}
        </Button>
      </span>
    </div>
  );
}
